/**
 * „So kam der Preis zustande“ und „Warum nichts geändert wurde“ – gemeinsam für Verlauf und
 * V&V-Tagesdetail. Jede Zeile stammt aus genau einem Feld der Entscheidung; fehlt das Feld, fehlt
 * die Zeile. Nichts wird nachgerechnet.
 */
import { formatMoney } from '@/shared/lib/format';
import { contributionLabel, minimumStayText, reasonText } from './pricingCodeText';

export interface DerivationSource {
  currency: string;
  outcome: string;
  reasonCode: string;
  minimumPrice: number | null;
  referencePrice: number | null;
  maximumPrice: number | null;
  unclampedContributionPercent: number | null;
  corridorContributionPercent: number | null;
  unclampedRulePrice: number | null;
  corridorPrice: number | null;
  movementPrice: number | null;
  hardClampedPrice: number | null;
  roundedDesiredPrice: number | null;
  minimumDelta: number | null;
  movementAnchorRate: number | null;
  /** `null`: die Beiträge liegen vor, sind aber nicht lesbar. */
  contributions: { code: string; adjustmentPercent: number }[] | null;
}

export interface Derivation {
  title: string;
  lines: string[];
  /** Roher Grund-Code, klein unter dem Satz. */
  code?: string;
}

const signed = (value: number) =>
  `${value > 0 ? '+' : value < 0 ? '−' : '±'}${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(Math.abs(value))} %`;

/** Ergebnisse, die einen Wert ändern; alle anderen erklärt der Grund. */
export const CHANGED = new Set(['Change', 'Target']);
const OUTCOME_LABEL: Record<string, string> = {
  Skip: 'Übersprungen',
  Unchanged: 'Unverändert',
  Locked: 'Gesperrt',
  Blocked: 'Blockiert',
};

export function priceDerivation(source: DerivationSource): Derivation {
  const lines = derivationLines(source);
  if (CHANGED.has(source.outcome)) return { title: 'So kam der Preis zustande', lines };
  // Auch ohne Änderung bleiben die gerechneten Werte sichtbar – unter dem Grund.
  const reason = reasonText(source.reasonCode) ?? minimumStayText(source.reasonCode);
  return {
    title: 'Warum nichts geändert wurde',
    lines: [
      `${OUTCOME_LABEL[source.outcome] ?? source.outcome}: ${reason ?? 'Grund des Servers'}`,
      ...lines,
    ],
    code: source.reasonCode,
  };
}

function derivationLines(source: DerivationSource) {
  const money = (value: number) => formatMoney(value, source.currency);
  const lines: string[] = [];
  const add = (value: number | null, line: (value: number) => string) => {
    if (value !== null) lines.push(line(value));
  };
  add(source.referencePrice, (value) => `Referenzpreis ${money(value)}`);
  if (source.contributions === null) lines.push('Beiträge: nicht lesbar');
  else if (source.contributions.length) {
    lines.push(
      `Beiträge: ${source.contributions.map((item) => `${contributionLabel(item.code)} ${signed(item.adjustmentPercent)}`).join(', ')}`,
    );
  }
  add(source.unclampedRulePrice, (value) =>
    source.unclampedContributionPercent === null
      ? `Regelpreis ${money(value)}`
      : `Regelpreis ${money(value)} (zusammen ${signed(source.unclampedContributionPercent)})`,
  );
  add(source.corridorPrice, (value) =>
    source.corridorContributionPercent === null
      ? `Korridor des Profils → ${money(value)}`
      : `Korridor des Profils (${signed(source.corridorContributionPercent)}) → ${money(value)}`,
  );
  add(source.movementPrice, (value) =>
    source.movementAnchorRate === null
      ? `Bewegungsgrenze → ${money(value)}`
      : `Bewegungsgrenze ab ${money(source.movementAnchorRate)} → ${money(value)}`,
  );
  // Die Grenzen stehen an jeder Entscheidung; das Ergebnis der Begrenzung nur, wenn gerechnet wurde.
  const { minimumPrice: min, maximumPrice: max, hardClampedPrice: clamped } = source;
  const bounds =
    min !== null && max !== null
      ? `${money(min)}–${money(max)}`
      : min !== null
        ? `ab ${money(min)}`
        : max !== null
          ? `bis ${money(max)}`
          : null;
  if (bounds || clamped !== null) {
    lines.push(
      [`Mindest- und Maximalpreis`, bounds, clamped !== null ? `→ ${money(clamped)}` : null]
        .filter(Boolean)
        .join(' '),
    );
  }
  add(source.roundedDesiredPrice, (value) => `Gerundet → ${money(value)}`);
  add(source.minimumDelta, (value) => `Mindeständerung ${money(value)}`);
  return lines;
}

/**
 * `contributionsJson` des Verlaufs ist PascalCase (`{"Code","AdjustmentPercent"}`, so speichert ihn
 * der Rechner). Ist es nicht lesbar, sagt die Herleitung das, statt die Beiträge wegzulassen.
 */
export function parseContributions(json: string): DerivationSource['contributions'] {
  try {
    const parsed: unknown = JSON.parse(json);
    if (!Array.isArray(parsed)) return null;
    const read = parsed.map((entry: Record<string, unknown> | null) => {
      const code = entry?.Code ?? entry?.code;
      const percent = entry?.AdjustmentPercent ?? entry?.adjustmentPercent;
      return typeof code === 'string' && typeof percent === 'number'
        ? { code, adjustmentPercent: percent }
        : null;
    });
    // Ein unlesbarer Eintrag macht die Beiträge unvollständig – das sagt die Herleitung.
    return read.every((entry) => entry !== null) ? read : null;
  } catch {
    return null;
  }
}
